import React from "react"
import Select, {
  components,
  SingleValueProps,
  OptionProps as RSOptionProps,
} from "react-select"

export type RiskLevel = "red" | "green" | null

type RiskOption = { label: string; value: RiskLevel; color: string | null }

const OPTIONS: ReadonlyArray<RiskOption> = [
  { label: "None", value: null, color: null },
  { label: "Red", value: "red", color: "#ef4444" },
  { label: "Green", value: "green", color: "#22c55e" },
]

const Swatch: React.FC<{ color: string | null; size?: number }> = ({
  color,
  size = 18,
}) => (
  <div
    style={{
      width: size * 1.6,
      height: size,
      borderRadius: 4,
      border: "1px solid #cbd5e1",
      background:
        color ??
        "linear-gradient(45deg,#f8fafc 25%,#e2e8f0 25%,#e2e8f0 50%,#f8fafc 50%,#f8fafc 75%,#e2e8f0 75%)",
      backgroundSize: color ? undefined : "10px 10px",
    }}
  />
)

// In the control: render nothing when value === null so the placeholder shows
const SingleValue = (props: SingleValueProps<RiskOption, false>) => (
  <components.SingleValue {...props}>
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <Swatch color={props.data.color} />
      <span>{props.data.label}</span>
    </div>
  </components.SingleValue>
)

// In the menu: swatch + label
const OptionRow = (props: RSOptionProps<RiskOption, false>) => (
  <components.Option {...props}>
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <Swatch color={props.data.color} />
      <span>{props.data.label}</span>
    </div>
  </components.Option>
)

type Props = {
  value: RiskLevel // <- no undefined
  onChange: (v: RiskLevel) => void // <- no undefined
}

export const RiskLevelSelect: React.FC<Props> = ({ value, onChange }) => {
  const selected =
    value === null ? null : OPTIONS.find((o) => o.value === value) ?? null

  return (
    <Select<RiskOption, false>
      placeholder="Select risk level"
      menuPortalTarget={document.body}
      menuPosition="fixed"
      menuShouldScrollIntoView={false}
      isClearable
      options={OPTIONS}
      value={selected}
      onChange={(opt) => onChange(opt ? opt.value : null)} // clear or "None" => null
      components={{
        SingleValue,
        Option: OptionRow,
        IndicatorSeparator: () => null,
      }}
      styles={{
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        control: (base) => ({ ...base, borderRadius: 8, minHeight: 40 }),
        placeholder: (base) => ({ ...base, color: "#9ca3af", fontSize: 14 }),
        dropdownIndicator: (base) => ({ ...base, paddingInline: 8 }),
      }}
    />
  )
}
